import type { VehicleValuationCostAddition } from "@/common/api/vehicle-valuation-cost-addition/vehicle-valuation-cost-addition.types";
import type { VehicleValuationCostWarranty } from "@/common/api/vehicle-valuation-cost-warranty/vehicle-valuation-cost-warranty.types";
import type { VehicleValuationPriceAdjustment } from "@/common/api/vehicle-valuation-price-adjustment/vehicle-valuation-price-adjustment.types";
import { isLocalizedNumberInput, parseLocalizedNumberInput } from "@/common/helpers/number-format";

export interface AdditionFormState {
  key: string;
  valueNumber: string;
}

export interface WarrantyFormState {
  upToKilowatts: string;
  price: string;
}

export interface PriceAdjustmentFormState {
  brand: string;
  adjustmentPercentage: string;
}

export const EMPTY_WARRANTY_FORM: WarrantyFormState = {
  upToKilowatts: "",
  price: "",
};

export const EMPTY_PRICE_ADJUSTMENT_FORM: PriceAdjustmentFormState = {
  brand: "",
  adjustmentPercentage: "",
};

export const MAX_VALIDITY_DAYS = 3650;

export function isNonNegativeNumber(value: string): boolean {
  return isLocalizedNumberInput(value, { allowNegative: false });
}

export function isSignedPercentage(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  const parsedValue = Number(trimmed);
  return Number.isFinite(parsedValue) && parsedValue >= -100;
}

export function isOptionalSignedPercentage(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return true;
  const parsedValue = Number(trimmed);
  return Number.isFinite(parsedValue) && parsedValue >= -100;
}

export function isOptionalNonNegativeInteger(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return true;
  return isLocalizedNumberInput(trimmed, { allowNegative: false, integerOnly: true });
}

export function isValidityDays(value: string): boolean {
  const validityDays = parseValidityDays(value);
  return validityDays >= 1 && validityDays <= MAX_VALIDITY_DAYS;
}

export function parseNonNegativeNumber(value: string): number {
  return parseLocalizedNumberInput(value, { allowNegative: false });
}

export function parseValidityDays(value: string): number {
  return parseLocalizedNumberInput(value, { allowNegative: false, integerOnly: true });
}

export function parseSignedPercentage(value: string): number {
  return Number(value.trim());
}

export function parseOptionalSignedPercentage(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  return Number(trimmed);
}

export function parseOptionalNonNegativeInteger(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  return parseLocalizedNumberInput(trimmed, { allowNegative: false, integerOnly: true });
}

export function toDraftNumber(value: number | null | undefined): string {
  return value == null ? "" : String(value);
}

export function toAdditionFormState(
  addition: Pick<VehicleValuationCostAddition, "key" | "valueNumber"> | undefined,
): AdditionFormState {
  if (!addition) {
    return { key: "", valueNumber: "" };
  }

  return {
    key: addition.key,
    valueNumber: addition.valueNumber == null ? "" : String(addition.valueNumber),
  };
}

export function buildAdditionFormState(
  additions: VehicleValuationCostAddition[] | undefined,
): Record<string, AdditionFormState> {
  return (additions ?? []).reduce<Record<string, AdditionFormState>>((nextState, addition) => {
    nextState[addition.key] = toAdditionFormState(addition);
    return nextState;
  }, {});
}

export function getAdditionKeys(
  additions: VehicleValuationCostAddition[] | undefined,
  draftRows: Record<string, AdditionFormState>,
): string[] {
  const seenKeys = new Set<string>();
  const keys: string[] = [];

  for (const addition of additions ?? []) {
    if (seenKeys.has(addition.key)) {
      continue;
    }

    seenKeys.add(addition.key);
    keys.push(addition.key);
  }

  for (const key of Object.keys(draftRows)) {
    if (seenKeys.has(key)) {
      continue;
    }

    seenKeys.add(key);
    keys.push(key);
  }

  return keys;
}

export function getAdditionInputId(key: string): string {
  return `addition-${key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

export function toWarrantyFormState(
  warranty: Pick<VehicleValuationCostWarranty, "price" | "upToKilowatts">,
): WarrantyFormState {
  return {
    upToKilowatts: toDraftNumber(warranty.upToKilowatts),
    price: toDraftNumber(warranty.price),
  };
}

export function toPriceAdjustmentFormState(
  priceAdjustment: Pick<VehicleValuationPriceAdjustment, "adjustmentPercentage" | "brand">,
): PriceAdjustmentFormState {
  return {
    brand: priceAdjustment.brand?.uuid ?? "",
    adjustmentPercentage: toDraftNumber(priceAdjustment.adjustmentPercentage),
  };
}

export function sortWarrantyBrackets(warranties: VehicleValuationCostWarranty[]): VehicleValuationCostWarranty[] {
  return [...warranties].sort((leftWarranty, rightWarranty) => {
    const leftLimit = leftWarranty.upToKilowatts ?? Number.MAX_SAFE_INTEGER;
    const rightLimit = rightWarranty.upToKilowatts ?? Number.MAX_SAFE_INTEGER;

    if (leftLimit === rightLimit) {
      return leftWarranty.price - rightWarranty.price;
    }

    return leftLimit - rightLimit;
  });
}

export function sortPriceAdjustments(
  priceAdjustments: VehicleValuationPriceAdjustment[],
): VehicleValuationPriceAdjustment[] {
  return [...priceAdjustments].sort((leftAdjustment, rightAdjustment) => {
    const leftBrandName = leftAdjustment.brand?.name ?? "";
    const rightBrandName = rightAdjustment.brand?.name ?? "";

    return leftBrandName.localeCompare(rightBrandName);
  });
}
